import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, CheckCircle2 } from 'lucide-react';
import { projectsService } from '../services/projects';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TestFeatureGuard from '../components/TestFeatureGuard';

const REPORT_LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

const UploadPage: React.FC = () => {
  const { t, language } = useLanguage();
  const isTr = language === 'tr';
  const [files, setFiles] = useState<File[]>([]);
  const [formatType, setFormatType] = useState<'Film Senaryosu' | 'Dizi Senaryosu' | null>(null);
  const [reportLanguage, setReportLanguage] = useState('Türkçe');
  const [generateReport, setGenerateReport] = useState(true);
  const [generateCharacterBreakdown, setGenerateCharacterBreakdown] = useState(false);
  const [generateSequentialBreakdown, setGenerateSequentialBreakdown] = useState(false);
  const [generateLocationBreakdown, setGenerateLocationBreakdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');
  const [progress, setProgress] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    if (droppedFiles.length > 0) setFiles(prev => [...prev, ...droppedFiles]);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (selectedFiles.length > 0) setFiles(prev => [...prev, ...selectedFiles]);
  };
  const removeFile = (index: number) => setFiles(prev => prev.filter((_, i) => i !== index));

  const toggleOption = (option: 'report' | 'characters' | 'sequential' | 'location') => {
    if (option === 'report') setGenerateReport(!generateReport);
    else if (option === 'characters') setGenerateCharacterBreakdown(!generateCharacterBreakdown);
    else if (option === 'sequential') setGenerateSequentialBreakdown(!generateSequentialBreakdown);
    else if (option === 'location') setGenerateLocationBreakdown(!generateLocationBreakdown);
  };

  useEffect(() => () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
  }, []);

  useEffect(() => {
    if (!currentTaskId) return;
    const pollTaskStatus = async () => {
      try {
        const status = await projectsService.getTaskStatus(currentTaskId);
        setProgress(status.progress || 0);
        setProcessingText(status.status || t.analyzing);

        if (status.state === 'SUCCESS') {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          setProgress(100); setShowProcessing(false); setCurrentTaskId(null);
          if (createdProjectId) setShowSuccessModal(true);
        } else if (status.state === 'FAILURE') {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          setError(status.error || t.uploadFailed);
          setShowProcessing(false); setCurrentTaskId(null); setLoading(false);
        }
      } catch (err) {
        console.error('Failed to poll task status:', err);
      }
    };
    pollingIntervalRef.current = setInterval(pollTaskStatus, 2000);
    pollTaskStatus();
    return () => {
      if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
    };
  }, [currentTaskId, createdProjectId, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return setError(t.pleaseSelectFile);
    if (!formatType) return setError(t.pleaseSelectContentType);
    if (!generateReport && !generateCharacterBreakdown && !generateSequentialBreakdown) {
      return setError(t.pleaseSelectAtLeastOneOutput);
    }

    setLoading(true);
    setError('');
    setShowProcessing(true);
    setProcessingText(files.length > 1 ? `${files.length} ${isTr ? 'dosya yükleniyor…' : 'files uploading…'}` : t.readingPdf);
    setProgress(10);

    try {
      const uploadPromises = files.map(async (file, index) => {
        const result = await projectsService.uploadProject(file, {
          format_type: formatType,
          report_language: reportLanguage,
          generate_report: generateReport,
          generate_character_breakdown: generateCharacterBreakdown,
          generate_bible: false,
          generate_scene_table: false,
          generate_sequential_breakdown: generateSequentialBreakdown,
          generate_location_breakdown: generateLocationBreakdown,
        });
        const fileProgress = 10 + ((index + 1) / files.length) * 50;
        setProgress(fileProgress);
        setProcessingText(`${index + 1}/${files.length} · ${file.name}`);
        return result;
      });
      const results = await Promise.all(uploadPromises);

      if (results.length === 1 && results[0]?.project_id) setCreatedProjectId(results[0].project_id);
      else setCreatedProjectId(null);
      setFiles([]);

      const lastResult = results[results.length - 1];
      if (lastResult?.task_id) {
        setProgress(60);
        setProcessingText(isTr ? 'Analiz işleniyor…' : 'Analysis running…');
        setCurrentTaskId(lastResult.task_id);
      } else {
        setProgress(100);
        setShowSuccessModal(true);
      }
    } catch (err: any) {
      let errorMessage = t.uploadFailed;
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) errorMessage = t.backendNotResponding;
      else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network Error') || err?.message?.includes('Failed to fetch')) errorMessage = t.backendConnectionError;
      else if (err?.response?.status === 401) errorMessage = t.authorizationError;
      else if (err?.response?.status === 413) errorMessage = t.fileTooLarge;
      else if (err?.response?.status === 422) errorMessage = t.invalidFileFormat;
      else if (err?.response?.data?.detail) errorMessage = err.response.data.detail;
      else if (err?.message) errorMessage = err.message;
      setError(errorMessage);
      setShowProcessing(false); setLoading(false);
      if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
      setCurrentTaskId(null);
    }
  };

  const isFormValid = files.length > 0 && formatType && (generateReport || generateCharacterBreakdown || generateSequentialBreakdown || generateLocationBreakdown);

  // Outputs definition
  const outputs: { key: 'report' | 'characters' | 'sequential' | 'location'; letter: string; label: string; cost: number; testOnly?: boolean }[] = [
    { key: 'report', letter: 'A', label: t.generateReport, cost: 1 },
    { key: 'characters', letter: 'K', label: t.generateCharacterBreakdown, cost: 1 },
    { key: 'sequential', letter: 'S', label: t.sequentialBreakdown, cost: 4 },
    { key: 'location', letter: 'M', label: isTr ? 'Mekan Dökümü' : 'Location Breakdown', cost: 1 },
  ];

  const isOptOn = (key: string) => ({
    report: generateReport, characters: generateCharacterBreakdown,
    sequential: generateSequentialBreakdown, location: generateLocationBreakdown,
  }[key]);

  const totalCost =
    (generateReport ? 1 : 0) +
    (generateCharacterBreakdown ? 1 : 0) +
    (generateSequentialBreakdown ? 4 : 0) +
    (generateLocationBreakdown ? 1 : 0);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'YENİ ANALİZ' : 'NEW ANALYSIS'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'YÜKLE · YAPILANDIR · BAŞLAT' : 'UPLOAD · CONFIGURE · RUN'}</span>
        </div>

        <div className="max-w-3xl mb-16">
          <h1 className="display-h display-lg mb-4">
            {isTr ? <>Senaryon, <em>aynaya</em> tutuluyor.</> : <>Your script, <em>held up</em> to the mirror.</>}
          </h1>
          <p className="font-prose text-[16px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'PDF yükle, türünü seç, hangi çıktıları istediğini işaretle. Mr.Mirror sayfayı sindirir, dakikalar içinde rapor verir.'
              : 'Upload a PDF, pick a type, mark the outputs you want. Mr.Mirror reads the page and returns a report in minutes.'}
          </p>
        </div>

        {error && (
          <div className="mb-10 p-5 border border-signal-red/40 bg-signal-red/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="eyebrow-mute mb-1 block text-signal-red">/ ERROR</span>
                <p className="font-prose text-[14px] text-signal-red leading-relaxed">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-signal-red/70 hover:text-signal-red shrink-0" aria-label="Kapat">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Form left (8 cols) */}
          <form onSubmit={handleSubmit} className="lg:col-span-8 space-y-12">
            {/* /01 Dropzone */}
            <section>
              <div className="flex items-baseline justify-between mb-5">
                <span className="eyebrow"><span className="text-accent">/01</span> · {t.file?.toUpperCase() || 'FILE'}</span>
                <span className="eyebrow-mute">{t.maxFileSize?.toUpperCase()}</span>
              </div>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border border-dashed transition-colors ${
                  isDragging
                    ? 'border-accent bg-accent-soft'
                    : files.length > 0
                    ? 'border-success bg-accent-soft'
                    : 'border-hairline hover:border-accent'
                }`}
              >
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                {files.length > 0 ? (
                  <div className="p-6 space-y-3">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between gap-4 pb-3 border-b border-hairline last:border-b-0 last:pb-0">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="font-mono text-[10px] tracking-wider text-ink-mute shrink-0">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                          <div className="min-w-0">
                            <p className="font-serif italic text-[17px] text-ink truncate">{file.name}</p>
                            <p className="font-mono text-[10px] tracking-wider uppercase text-ink-mute mt-0.5">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                          className="w-8 h-8 flex items-center justify-center text-ink-mute hover:text-signal-red border border-transparent hover:border-signal-red transition-colors shrink-0"
                          aria-label="Kaldır"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {files.length > 1 && (
                      <p className="pt-3 mt-3 border-t border-hairline font-mono text-[10px] tracking-wider uppercase text-ink-mute">
                        {files.length} {isTr ? 'DOSYA SEÇİLDİ' : 'FILES SELECTED'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="py-16 px-6 text-center">
                    <Upload className="w-8 h-8 text-ink-mute mx-auto mb-4" />
                    <p className="font-serif italic text-2xl text-ink mb-2">
                      {t.uploadPdf}
                    </p>
                    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute">
                      {t.dragDropPdf} · PDF
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* /02 Format */}
            <section>
              <div className="flex items-baseline justify-between mb-5">
                <span className="eyebrow"><span className="text-accent">/02</span> · {t.contentType?.toUpperCase() || 'TYPE'}</span>
                <span className="eyebrow-mute">{isTr ? 'BİR TANE SEÇ' : 'PICK ONE'}</span>
              </div>
              <div className="grid grid-cols-2 gap-px bg-hairline border border-hairline">
                {([
                  { value: 'Film Senaryosu', letter: 'F', label: t.film },
                  { value: 'Dizi Senaryosu', letter: 'D', label: t.series },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormatType(opt.value)}
                    className={`p-8 text-left transition-colors ${
                      formatType === opt.value ? 'bg-accent-soft' : 'bg-background hover:bg-accent-soft'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className={`font-serif italic text-5xl leading-none ${formatType === opt.value ? 'text-accent' : 'text-ink-mute group-hover:text-accent'}`}>
                        {opt.letter}
                      </span>
                      <span className={`w-5 h-5 border flex items-center justify-center ${
                        formatType === opt.value ? 'border-accent' : 'border-hairline'
                      }`}>
                        {formatType === opt.value && <CheckCircle2 className="w-3 h-3 text-accent" />}
                      </span>
                    </div>
                    <span className={`font-mono text-[11px] tracking-[0.18em] uppercase ${
                      formatType === opt.value ? 'text-ink' : 'text-ink-dim'
                    }`}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            {/* /03 Outputs */}
            <section>
              <div className="flex items-baseline justify-between mb-5">
                <span className="eyebrow"><span className="text-accent">/03</span> · {t.analysisOptions?.toUpperCase() || 'OUTPUTS'}</span>
                <span className="eyebrow-mute">{isTr ? 'EN AZ BİR ÇIKTI' : 'AT LEAST ONE'}</span>
              </div>
              <div className="border border-hairline">
                {outputs.map((opt, i) => {
                  const inner = (
                    <button
                      type="button"
                      onClick={() => toggleOption(opt.key)}
                      className={`w-full grid grid-cols-12 items-center gap-4 px-5 py-5 text-left transition-colors hover:bg-accent-soft ${
                        i < outputs.length - 1 ? 'border-b border-hairline' : ''
                      } ${isOptOn(opt.key) ? 'bg-accent-soft' : ''}`}
                    >
                      <span className="col-span-1 font-mono text-[10px] tracking-wider text-ink-mute">
                        /{String(i + 1).padStart(2, '0')}
                      </span>
                      <span className={`col-span-1 font-serif italic text-3xl leading-none ${isOptOn(opt.key) ? 'text-accent' : 'text-ink-mute'}`}>
                        {opt.letter}
                      </span>
                      <span className={`col-span-7 md:col-span-8 font-prose text-[15px] ${isOptOn(opt.key) ? 'text-ink' : 'text-ink-dim'}`}>
                        {opt.label}
                      </span>
                      <span className="col-span-2 md:col-span-1 font-mono text-[11px] tracking-wider text-ink-mute tabular-nums text-right">
                        {opt.cost} {isTr ? 'KR' : 'TK'}
                      </span>
                      <span className={`col-span-1 w-5 h-5 border flex items-center justify-center justify-self-end ${
                        isOptOn(opt.key) ? 'border-accent bg-accent' : 'border-hairline'
                      }`}>
                        {isOptOn(opt.key) && <CheckCircle2 className="w-3 h-3 text-accent-ink" />}
                      </span>
                    </button>
                  );
                  return opt.testOnly ? (
                    <TestFeatureGuard key={opt.key}>{inner}</TestFeatureGuard>
                  ) : (
                    <React.Fragment key={opt.key}>{inner}</React.Fragment>
                  );
                })}
              </div>
            </section>

            {/* /04 Language */}
            <section>
              <div className="flex items-baseline justify-between mb-5">
                <span className="eyebrow"><span className="text-accent">/04</span> · {t.reportLanguage?.toUpperCase() || 'LANGUAGE'}</span>
                <span className="eyebrow-mute">{isTr ? 'ÇIKTI DİLİ' : 'OUTPUT LOCALE'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-hairline border border-hairline">
                {REPORT_LANGUAGES.map((lang) => {
                  const active = reportLanguage === lang.name;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => setReportLanguage(lang.name)}
                      className={`p-4 text-left transition-colors ${active ? 'bg-accent-soft' : 'bg-background hover:bg-accent-soft'}`}
                    >
                      <span className="text-xl block mb-2">{lang.flag}</span>
                      <span className={`font-mono text-[11px] tracking-[0.12em] uppercase ${active ? 'text-accent' : 'text-ink-dim'}`}>
                        {lang.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          </form>

          {/* Summary right (4 cols) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 border border-hairline">
              <div className="p-6 border-b border-hairline">
                <span className="eyebrow mb-2 block">/ {isTr ? 'ÖZET' : 'SUMMARY'}</span>
                <h3 className="font-serif italic text-2xl text-ink leading-tight">
                  {isTr ? <>Üretim <em>siparişi.</em></> : <>Production <em>order.</em></>}
                </h3>
              </div>

              <dl className="divide-y divide-hairline">
                <div className="flex items-center justify-between px-6 py-4">
                  <dt className="eyebrow-mute">{t.file?.toUpperCase()}</dt>
                  <dd className={`font-mono text-[11px] tracking-wider uppercase ${files.length > 0 ? 'text-success' : 'text-ink-mute'}`}>
                    {files.length > 0 ? `✓ ${files.length}` : '—'}
                  </dd>
                </div>
                <div className="flex items-center justify-between px-6 py-4">
                  <dt className="eyebrow-mute">{t.contentType?.toUpperCase()}</dt>
                  <dd className={`font-mono text-[11px] tracking-wider uppercase ${formatType ? 'text-success' : 'text-ink-mute'}`}>
                    {formatType ? (formatType === 'Film Senaryosu' ? t.film : t.series) : '—'}
                  </dd>
                </div>
                <div className="flex items-center justify-between px-6 py-4">
                  <dt className="eyebrow-mute">{t.reportLanguage?.toUpperCase()}</dt>
                  <dd className="font-mono text-[11px] tracking-wider uppercase text-ink">
                    {reportLanguage}
                  </dd>
                </div>
                <div className="flex items-center justify-between px-6 py-4">
                  <dt className="eyebrow-mute">{isTr ? 'ÇIKTI' : 'OUTPUTS'}</dt>
                  <dd className="font-mono text-[11px] tracking-wider uppercase text-ink-dim text-right">
                    {[
                      generateReport && 'A',
                      generateCharacterBreakdown && 'K',
                      generateSequentialBreakdown && 'S',
                      generateLocationBreakdown && 'M',
                    ].filter(Boolean).join(' · ') || '—'}
                  </dd>
                </div>
                <div className="flex items-center justify-between px-6 py-4 bg-accent-soft">
                  <dt className="eyebrow text-accent">{isTr ? 'TOPLAM' : 'TOTAL'}</dt>
                  <dd className="font-serif italic text-2xl text-accent leading-none">
                    {totalCost}
                    <span className="font-mono not-italic text-[10px] text-ink-dim ml-1.5 tracking-wider align-top">
                      {isTr ? 'KREDİ' : 'TK'}
                    </span>
                  </dd>
                </div>
              </dl>

              <div className="p-6">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isFormValid || loading}
                  className="btn-accent w-full justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t.uploading : t.startAnalysis}
                  {!loading && isFormValid && <span aria-hidden>→</span>}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Processing modal */}
      {showProcessing && (() => {
        const milestones = [
          { label: isTr ? 'Yükleme' : 'Upload', threshold: 10 },
          { label: isTr ? 'Metin Çıkarma' : 'Extract', threshold: 40 },
          { label: isTr ? 'Analiz' : 'Analyze', threshold: 70 },
          { label: isTr ? 'Kayıt' : 'Save', threshold: 95 },
        ];
        return (
          <div className="fixed inset-0 bg-background/85 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="relative w-full sm:max-w-md bg-elevated border-t sm:border border-hairline px-7 py-7 shadow-2xl">
              <button
                onClick={() => setShowProcessing(false)}
                className="absolute top-4 right-4 text-ink-mute hover:text-ink transition-colors"
                aria-label="Kapat"
              >
                <X className="w-5 h-5" />
              </button>
              <span className="eyebrow mb-2 block">/ {isTr ? 'İŞLENİYOR' : 'PROCESSING'}</span>
              <h3 className="display-h text-2xl mb-2">
                {isTr ? <>Analizin <em>hazırlanıyor.</em></> : <>Your analysis <em>is running.</em></>}
              </h3>
              <p className="font-prose text-[14px] text-ink-dim mb-6">
                {processingText || t.analyzing}
              </p>

              <div className="w-full bg-hairline h-px mb-1 relative overflow-hidden">
                <div className="bg-accent h-px transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="flex justify-between font-mono text-[10px] tracking-wider uppercase text-ink-mute mb-6">
                <span>{progress}%</span>
                <span>100%</span>
              </div>

              <ul className="space-y-3 mb-6">
                {milestones.map((m, idx) => {
                  const done = progress >= m.threshold;
                  const active = !done && progress >= (milestones[idx - 1]?.threshold ?? 0);
                  return (
                    <li key={m.label} className="flex items-center gap-3 font-mono text-[11px] tracking-wider uppercase">
                      <span className={`w-5 h-5 border flex items-center justify-center text-[9px] shrink-0 ${
                        done ? 'border-accent bg-accent text-accent-ink' : active ? 'border-accent text-accent' : 'border-hairline text-ink-mute'
                      }`}>
                        {done ? '✓' : active ? '·' : ''}
                      </span>
                      <span className={done ? 'text-ink' : active ? 'text-accent' : 'text-ink-mute'}>
                        {m.label}
                      </span>
                      {active && <span className="ml-auto w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />}
                    </li>
                  );
                })}
              </ul>

              <p className="font-mono text-[10px] tracking-wider uppercase text-ink-mute text-center">
                {isTr ? 'BU PENCEREYİ KAPATABİLİRSİN — İŞLEM SÜRER' : 'YOU CAN CLOSE THIS — IT KEEPS RUNNING'}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Success modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/85 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-elevated border border-hairline px-8 py-8 max-w-md w-full">
            <span className="eyebrow text-success mb-3 block">/ {isTr ? 'TAMAMLANDI' : 'COMPLETE'}</span>
            <h2 className="display-h text-3xl mb-3">
              {isTr ? <>Analiz <em>hazır.</em></> : <>Analysis <em>ready.</em></>}
            </h2>
            <p className="font-prose text-[14px] text-ink-dim leading-relaxed mb-8">
              {files.length > 1
                ? (isTr ? 'Dosyalar yüklendi. Analizler arka planda işleniyor.' : 'Files uploaded. Analyses running in the background.')
                : t.analysisCompletedDesc}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  if (createdProjectId) navigate(`/projects/${createdProjectId}`);
                  else navigate('/dashboard');
                }}
                className="btn-accent w-full justify-center"
              >
                {t.viewAnalysisResults}
                <span aria-hidden>→</span>
              </button>
              <button onClick={() => setShowSuccessModal(false)} className="btn-ghost w-full justify-center">
                {t.back}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default UploadPage;
